import React, { useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { useQuery } from "@apollo/client";
import moment from "moment";

import { BORDER_COLOR, TEXT_MUTED, SummaryCard } from "./Shared.component";
import Theme from "../utils/Theme";
import { SERVICE_APPOINTMENTS_REPORT } from "../graphql/query/query";

// Header background (same as Manage User table header)
const HEADER_BG = "#F4F6F8";

// Card shadow used by the Manage User table
const PAPER_SHADOW =
  "0px 0px 2px 0px rgba(0, 171, 85, 0.2),0px 12px 24px -4px rgba(0, 171, 85, 0.12)";

/* ---------- helpers ---------- */

const isEmpty = (v) => v === null || v === undefined || v === "";
const dash = (v) => (isEmpty(v) ? "-" : v);
const formatDate = (v) => (v ? moment(v).format("MM/DD/YYYY") : "-");

const norm = (v) => String(v ?? "").trim().toLowerCase();

// Abbreviations: [shortLabel, fullLabel]. Unknown values pass through as-is.
const SOURCE_MAP = {
  delivery: "Deliv",
  "follow up": "F/U",
  "follow-up": "F/U",
  followup: "F/U",
};
const TYPE_MAP = {
  purchased: "Purch",
  // household: "HH",
  "household vehicle": "HH",
};

const abbreviate = (map) => (v) => (isEmpty(v) ? "-" : map[norm(v)] || v);

// Hidden text keeps the word in the DOM so copy/paste and table exports still
// capture "Scheduled" / "Not Scheduled" even though only an icon is shown.
const srOnly = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

const StatusIcon = ({ value }) => {
  if (isEmpty(value)) return "-";
  const scheduled = norm(value) === "scheduled";
  const Icon = scheduled ? CheckCircleIcon : CancelIcon;
  return (
    <Box component="span" sx={{ display: "inline-flex", position: "relative" }}>
      <Icon
        sx={{ fontSize: 18, color: scheduled ? "#2E7D32" : "#D32F2F" }}
        aria-hidden
      />
      <Box component="span" sx={srOnly}>
        {value}
      </Box>
    </Box>
  );
};

/* ---------- column config ----------
   text:  value shown in the cell
   full:  full value shown in the hover tooltip
   w:     max width (px) before the text truncates with an ellipsis
*/
const COLUMNS = [
  { key: "purchaseDate", label: "Purchase Date", w: 100,
    text: (r) => formatDate(r.purchaseDate) },
  { key: "customer", label: "Customer", w: 130, bold: true,
    text: (r) => dash(r.customer) },
  { key: "cell", label: "Cell", w: 110, text: (r) => dash(r.cell) },
  { key: "email", label: "Email", w: 165, text: (r) => dash(r.email) },
  { key: "sales", label: "Sales", w: 100, text: (r) => dash(r.sales) },
  { key: "year", label: "Year", w: 50, v: true, text: (r, v) => dash(v.year) },
  { key: "make", label: "Make", w: 90, v: true, text: (r, v) => dash(v.make) },
  { key: "model", label: "Model", w: 100, v: true, text: (r, v) => dash(v.model) },
  { key: "type", label: "Type", w: 60, v: true,
    text: (r, v) => abbreviate(TYPE_MAP)(v.type),
    full: (r, v) => dash(v.type) },
  { key: "status", label: "Status", w: 60, v: true, align: "center",
    render: (r, v) => <StatusIcon value={v.status} />,
    full: (r, v) => dash(v.status) },
  { key: "source", label: "Source", w: 60, v: true,
    text: (r, v) => abbreviate(SOURCE_MAP)(v.source),
    full: (r, v) => dash(v.source) },
  { key: "set", label: "Set", w: 85, v: true, text: (r, v) => formatDate(v.setDate) },
  { key: "appt", label: "Appt", w: 85, v: true, text: (r, v) => formatDate(v.apptDate) },
  { key: "time", label: "Time", w: 75, v: true, text: (r, v) => dash(v.apptTime) },
  { key: "method", label: "Method", w: 130, v: true, text: (r, v) => dash(v.method) },
];

const LINE_H = 24; // fixed line height keeps stacked vehicle lines aligned across columns

const Line = ({ col, row, vehicle }) => {
  const text = col.text ? col.text(row, vehicle) : null;
  const full = col.full ? col.full(row, vehicle) : text;
  const content = col.render ? col.render(row, vehicle) : text;
  return (
    <Box
      title={full && full !== "-" ? String(full) : ""}
      sx={{
        height: LINE_H,
        lineHeight: `${LINE_H}px`,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {content}
    </Box>
  );
};

const Cell = ({ col, row }) => {
  const vehicles =
    Array.isArray(row.vehicles) && row.vehicles.length > 0 ? row.vehicles : [{}];
  return (
    <TableCell
      align={col.align}
      sx={{
        fontSize: "13px",
        maxWidth: col.w,
        width: col.w,
        verticalAlign: "top",
        fontWeight: col.bold ? 600 : 400,
        color: col.bold ? Theme.palette.primary.main : "inherit",
      }}
    >
      {col.v ? (
        vehicles.map((vehicle, i) => (
          <Line key={i} col={col} row={row} vehicle={vehicle} />
        ))
      ) : (
        <Line col={col} row={row} vehicle={{}} />
      )}
    </TableCell>
  );
};

/* ---------- component ---------- */

const ServiceAppointmentsTable = ({
  dealershipId,
  employeeUserId,
  isEmployeeOnly,
  dateRange,
  timezone,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, loading, error } = useQuery(SERVICE_APPOINTMENTS_REPORT, {
    skip: !dealershipId,
    fetchPolicy: "network-only",
    variables: {
      reportInput: {
        dealership_id: dealershipId,
        employee_user_id: employeeUserId,
        isEmployeeOnly: !!isEmployeeOnly,
        f_startDate: dateRange?.startDate,
        f_endDate: dateRange?.endDate,
        timezone,
        pageCount: page + 1,
        rowsPerPage,
      },
    },
  });

  const report = data?.serviceAppointmentsReport;
  const apiErrors = report?.errors;
  const summaryData = report?.summary;
  const rows = report?.rows || [];
  const total = report?.total || 0;

  const summary = summaryData
    ? [
        { label: "Deliveries", value: String(summaryData.deliveries ?? 0) },
        { label: "Appointments Created", value: String(summaryData.appointmentsCreated ?? 0) },
        { label: "At Delivery", value: String(summaryData.atDelivery ?? 0) },
        { label: "Follow Up Generated", value: String(summaryData.followUpGenerated ?? 0) },
      ]
    : [];

  return (
    // FIX: the table has minWidth 1300, which was widening the whole page
    // (so the right padding disappeared). "contain: inline-size" makes this
    // wrapper report zero intrinsic width to its parents, so it simply fills
    // the space the page gives it and the table scrolls inside its own card.
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        contain: "inline-size",
      }}
    >
      <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap", mb: 2 }}>
        {summary.map((s) => (
          <SummaryCard key={s.label} label={s.label} value={s.value} />
        ))}
      </Box>

      {(error || apiErrors?.length > 0) && (
        <Typography sx={{ fontSize: "13px", color: "error.main", mb: 1 }}>
          {error?.message ||
            apiErrors?.[0]?.message ||
            "Unable to load Service Appointments report."}
        </Typography>
      )}

      {/* Table — same card look as the Manage User table */}
      <Paper
        elevation={0}
        sx={{
          boxShadow: PAPER_SHADOW,
          borderRadius: "14px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <TableContainer
          sx={{
            // Fixed height: pagination stays in the same place whether there
            // are 1 or 100 rows. Rows scroll inside when they don't fit.
            height: "calc(100vh - 430px)",
            minHeight: "250px",
            width: "100%",
            maxWidth: "100%",
            overflow: "auto", // horizontal scroll lives here, not on the page
            "& .MuiTableCell-root": {
              padding: "6px 8px",
              textAlign: "center",
            },
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 1280 }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    sx={{
                      width: col.w,
                      fontWeight: 700,
                      fontSize: "12px",
                      color: "text.primary",
                      backgroundColor: HEADER_BG,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    sx={{ textAlign: "center", py: 4, color: TEXT_MUTED, fontSize: "13px" }}
                  >
                    No Service Appointments records found for this range.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => (
                  <TableRow hover key={idx}>
                    {COLUMNS.map((col) => (
                      <Cell key={col.key} col={col} row={row} />
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 8px",
            borderTop: `1px solid ${BORDER_COLOR}`,
            backgroundColor: "#fff",
            "& .MuiTablePagination-toolbar": { minHeight: "40px", padding: 0 },
          }}
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ServiceAppointmentsTable;